import {ID_PREFIX} from "./constants";
import {createStore, del as idbDel, get as idbGet, set as idbSet, update} from "idb-keyval";
import {debug} from "./log";

const customStore = createStore(ID_PREFIX + 'storage', 'storage');

export function get(key: string) {
    return idbGet(key, customStore)
}

export function del(key: string) {
    return idbDel(key, customStore)
}

export function set(key: string, value: any) {
    return idbSet(key, value, customStore)
}

const me = crypto.randomUUID();

interface LockValue {
    id: string;
    timestamp: number;
}

// locks expire after 30 seconds. They can be extended, though.
const LOCK_TIMEOUT_MS = 30000;

export async function lock(key: string): Promise<boolean> {
    let hasLock = false;

    try {
        await update(key, (oldValue: LockValue | undefined | false) => {
            const now = Date.now();
            // Check if lock is held by another process and still valid
            if (oldValue && typeof oldValue === 'object' &&
                oldValue.id !== me &&
                oldValue.timestamp + LOCK_TIMEOUT_MS > now) {
                debug(`Lock ${key} held by ${oldValue.id}, not getting lock for ${me}...`);
                return oldValue;
            }

            debug(`Lock ${key} not held, getting lock for ${me}...`);
            hasLock = true;
            return {id: me, timestamp: now} as LockValue;
        }, customStore);
    } catch (error) {
        debug(`Error acquiring lock ${key}:`, error);
        hasLock = false;
    }

    return hasLock;
}

export async function extendLock(key: string) {
    let hasLock = false;

    try {
        await update(key, (oldValue: LockValue | undefined | false) => {
            const now = Date.now();
            if (oldValue && typeof oldValue === 'object' &&
                oldValue.id === me &&
                oldValue.timestamp + LOCK_TIMEOUT_MS > now) {
                debug(`Lock ${key} already held by ${oldValue.id}, extending...`);
                hasLock = true;
                return {id: me, timestamp: now};
            } else {
                debug(`Lock ${key} held by ${oldValue ? oldValue.id : 'unknown'}, not extending for ${me}...`);
                return oldValue || false;
            }
        }, customStore);
    } catch (error) {
        debug(`Error acquiring lock ${key}:`, error);
        hasLock = false;
    }

    return hasLock;
}

export async function unlock(key: string) {
    await update(key, (oldValue: LockValue | undefined | false) => {
        if (!oldValue || typeof oldValue !== 'object' || !('id' in oldValue)) {
            debug(`Lock ${key} not found or invalid, nothing to unlock for ${me}...`);
            return false;
        }

        if (oldValue.id === me) {
            debug(`Lock ${key} held by ${me}, releasing lock...`);
            return false;
        }

        debug(`Lock ${key} held by ${oldValue.id}, not releasing lock for ${me}...`);
        return oldValue;
    }, customStore);
}